import { useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Modal, Radio, Typography } from "antd";
import type { AmendmentResponsibleSnapshot } from "@/features/journal-version-list";
import "./amendment-responsible-picker.scss";

interface AmendmentResponsiblePickerProps {
  options: AmendmentResponsibleSnapshot[];
  value?: string;
  onChange: (responsible: AmendmentResponsibleSnapshot) => void;
}

export const AmendmentResponsiblePicker = ({
  options,
  value,
  onChange,
}: AmendmentResponsiblePickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(
    options.find((option) => option.name === value)?.id ?? null,
  );
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = options.filter((option) =>
    [option.name, option.position, option.organization, option.email].some(
      (field) => field.toLowerCase().includes(normalizedSearch),
    ),
  );

  const handleConfirm = () => {
    const selected = options.find((option) => option.id === selectedId);
    if (!selected) return;
    onChange(selected);
    setOpen(false);
  };

  const handleOpen = () => {
    setSelectedId(options.find((option) => option.name === value)?.id ?? null);
    setSearch("");
    setOpen(true);
  };

  return (
    <>
      <Button
        className="amendment-responsible-picker__trigger"
        type="text"
        icon={<PlusOutlined />}
        onClick={handleOpen}
      >
        {value || "Добавить сотрудника"}
      </Button>
      <Modal
        className="amendment-responsible-picker"
        title="Добавить сотрудника"
        width={640}
        open={open}
        onCancel={() => setOpen(false)}
        okText="Добавить"
        cancelText="Отменить"
        okButtonProps={{ disabled: !selectedId }}
        onOk={handleConfirm}
        destroyOnClose
      >
        <div className="amendment-responsible-picker__body">
          <Input.Search
            allowClear
            placeholder="Поиск по ФИО, должности или организации"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {filteredOptions.length > 0 ? (
            <Radio.Group
              className="amendment-responsible-picker__options"
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {filteredOptions.map((option) => (
                <Radio
                  className="amendment-responsible-picker__option"
                  value={option.id}
                  key={option.id}
                >
                  <span className="amendment-responsible-picker__option-content">
                    <Typography.Text strong>{option.name}</Typography.Text>
                    <span className="amendment-responsible-picker__option-details">
                      <Typography.Text type="secondary">
                        {option.position}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {option.organization}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {option.email}
                      </Typography.Text>
                    </span>
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          ) : (
            <Empty description="Сотрудники не найдены" />
          )}
        </div>
      </Modal>
    </>
  );
};
